import CloseIcon from '@mui/icons-material/Close';
import { Chip, Stack, Typography } from '@mui/material';
import { MAX_SMS_SESSION_RECIPIENTS } from '@provision/conversation-types';
import { toNationalPhoneDigits } from '@provision/formatting-utils';
import { useState } from 'react';
import { formatToPhoneNumber } from 'src/components/FormattedPhoneInput/format-to-phone-number';
import { SelectMenu } from 'src/components/SelectMenu';
import type { SelectMenuOption } from 'src/components/SelectMenu/select-menu.types';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import { participantChipSx } from 'src/pages/Conversations/Chat/ParticipationPanel/participant-chip.styles';
import { SmsLiveDot } from 'src/pages/Conversations/Chat/SmsSession/sms-live-dot';
import {
  getNumberStatus,
  type SmsNumberOption,
  type SmsRecipient,
} from 'src/pages/Conversations/Chat/SmsSession/sms-recipients';
import type { SmsRecipientsModel } from './hooks/use-sms-recipients';

function toMenuOption(option: SmsNumberOption, added: boolean): SelectMenuOption {
  const status = getNumberStatus(option.verdict);
  const { recipient } = option;
  return {
    value: recipient.digits,
    title: formatToPhoneNumber(recipient.digits),
    description: recipient.kind === 'clientPhone' ? recipient.label : undefined,
    group: recipient.name ?? formatToPhoneNumber(recipient.digits),
    hint: added ? 'Added' : (status.note ?? undefined),
    disabled: added || status.disabled,
  };
}

const SendsFrom = ({ line }: { line: SmsRecipientsModel['sendsFrom'] }) =>
  line ? (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'block', px: 1.5, py: 0.75 }}
    >
      Sends from {formatToPhoneNumber(line.phoneNumber)}
      {line.brandName ? ` · ${line.brandName}` : ''}
    </Typography>
  ) : null;

const RemoveIcon = (
  <Tooltip title="Remove" enterDelay={3000}>
    <CloseIcon aria-label="Remove" />
  </Tooltip>
);

/** one recipient: a chip, and for a person on record a menu of their numbers */
const SmsRecipientChip = ({
  recipient,
  index,
  model,
  removable,
}: {
  recipient: SmsRecipient;
  index: number;
  model: SmsRecipientsModel;
  removable: boolean;
}) => {
  const personOptions = recipient.participantId
    ? model.options.filter(
        (option) => option.recipient.participantId === recipient.participantId,
      )
    : [];
  const inText = new Set(model.recipients.map((other) => other.digits));
  const number = formatToPhoneNumber(recipient.digits);
  const label = recipient.name ? `${recipient.name} · ${number}` : number;
  const onDelete = removable ? () => void model.removeRecipient(index) : undefined;

  if (!personOptions.length) {
    return (
      <Chip label={label} onDelete={onDelete} deleteIcon={RemoveIcon} sx={participantChipSx} />
    );
  }

  return (
    <SelectMenu
      label={`${recipient.name ?? 'Recipient'} numbers`}
      sectioned
      options={personOptions.map((option) =>
        toMenuOption(
          option,
          option.recipient.digits !== recipient.digits && inText.has(option.recipient.digits),
        ),
      )}
      value={recipient.digits}
      onChange={(digits) => {
        const picked = personOptions.find((option) => option.recipient.digits === digits);
        if (picked) void model.replaceRecipient(index, picked.recipient);
      }}
      onOpenChange={(open) => {
        if (open) model.checkNumbers(personOptions);
      }}
      renderTrigger={({ getTriggerProps }) => (
        <Chip
          {...getTriggerProps()}
          clickable
          label={label}
          onDelete={onDelete}
          deleteIcon={RemoveIcon}
          sx={participantChipSx}
        />
      )}
    >
      {/* rendered after the option list inside the Popover — the menu footer */}
      <SendsFrom line={model.sendsFrom} />
    </SelectMenu>
  );
};

/** "+ Add": the people on the conversation and their numbers, or a typed number */
const SmsAddRecipient = ({ model }: { model: SmsRecipientsModel }) => {
  const [query, setQuery] = useState('');
  const inText = new Set(model.recipients.map((recipient) => recipient.digits));

  // people not yet in the text first, then the spare numbers of those who are
  const peopleInText = new Set(model.recipients.map((recipient) => recipient.participantId));
  const sorted = [...model.options].sort(
    (a, b) =>
      Number(peopleInText.has(a.recipient.participantId)) -
      Number(peopleInText.has(b.recipient.participantId)),
  );
  const needle = query.trim().toLowerCase();
  const digitsNeedle = needle.replace(/\D/g, '');
  const listed = needle
    ? sorted.filter((option) =>
        `${option.recipient.name ?? ''} ${option.recipient.digits}`
          .toLowerCase()
          .includes(digitsNeedle || needle),
      )
    : sorted;

  const typedDigits = toNationalPhoneDigits(query);
  const createAction =
    typedDigits &&
    !inText.has(typedDigits) &&
    !model.options.some((option) => option.recipient.digits === typedDigits)
      ? {
          label: `+ Text ${formatToPhoneNumber(typedDigits)}`,
          onSelect: () => {
            void model.addRecipient({
              kind: 'typed',
              digits: typedDigits,
              name: null,
              participantId: null,
            });
            setQuery('');
          },
        }
      : undefined;

  return (
    <Tooltip
      title={
        model.canAdd ? '' : `A group text takes up to ${MAX_SMS_SESSION_RECIPIENTS} numbers`
      }
      disableHoverListener={model.canAdd}
    >
      <span>
        <SelectMenu
          label="Add a number"
          sectioned
          searchPlaceholder="Search people or type a number"
          searchValue={query}
          onSearchChange={setQuery}
          disabled={!model.canAdd}
          options={listed.map((option) =>
            toMenuOption(option, inText.has(option.recipient.digits)),
          )}
          value={null}
          onChange={(digits) => {
            const picked = model.options.find((option) => option.recipient.digits === digits);
            if (picked) void model.addRecipient(picked.recipient);
          }}
          onOpenChange={(open) => {
            if (open) model.checkNumbers(listed);
            else setQuery('');
          }}
          createAction={createAction}
          renderTrigger={({ getTriggerProps, disabled }) => (
            <Typography
              {...getTriggerProps()}
              component="button"
              type="button"
              variant="body2"
              disabled={disabled}
              sx={{
                border: 0,
                background: 'none',
                cursor: disabled ? 'default' : 'pointer',
                color: 'text.secondary',
                px: 0.5,
              }}
            >
              + Add
            </Typography>
          )}
        >
          <SendsFrom line={model.sendsFrom} />
        </SelectMenu>
      </span>
    </Tooltip>
  );
};

export const SmsRecipientLine = ({ model }: { model: SmsRecipientsModel }) => (
  <Stack
    direction="row"
    alignItems="center"
    flexWrap="wrap"
    gap={0.75}
    sx={{ minHeight: '1.75rem' }}
  >
    <Typography variant="body2" color="text.secondary">
      To
    </Typography>
    {model.isLocked ? (
      <SmsLiveDot
        data-testid="sms-live-dot"
        sx={{ width: '0.4375rem', height: '0.4375rem' }}
      />
    ) : null}
    {model.recipients.map((recipient, index) => (
      <SmsRecipientChip
        key={recipient.digits}
        recipient={recipient}
        index={index}
        model={model}
        removable={model.recipients.length > 1}
      />
    ))}
    <SmsAddRecipient model={model} />
  </Stack>
);
